import { Button } from "web-design-system";
import { useRouter, useSearch } from "@tanstack/react-router";
import omit from "lodash/omit";
import { useAuthorizeMandate, useClient } from "creso-core/hooks";
import type { Mandate } from "creso-core/types";
import { toast } from "sonner";
import { CreateMandateForm } from "web-core";
import { handleError } from "@/utils/error";
import { Route } from "@/routes/__root";
import MandateLayout from "./mandate-layout";

/**
 * Partners adapter for the shared create-mandate form: supplies the page chrome
 * and, once created, emails the client an authorisation link before returning
 * to the list through search-param navigation.
 */
export default function CreateMandate() {
  const router = useRouter();
  const { client_id = 0 } = useSearch({ from: Route.id });
  const { data: client } = useClient(client_id);
  const { mutateAsync: authorizeMandate } = useAuthorizeMandate();

  const investmentAccountGuid = client?.investment_account ?? "";

  const goBack = () =>
    router.navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => omit(prev, ["context"]),
      replace: true,
    });

  const onCreate = async (mandate: Mandate) => {
    try {
      await authorizeMandate({
        id: mandate.id,
        investment_account_guid: investmentAccountGuid,
        sendEmail: true,
      });
    } catch (error) {
      handleError(error, "Failed to authorize mandate");
    }

    toast.success("Mandate created successfully");
    goBack();
  };

  return (
    <CreateMandateForm
      investmentAccountId={investmentAccountGuid}
      onCreate={onCreate}
    >
      {({ fields, submit, isSubmitting }) => (
        <MandateLayout
          title="Create Mandate"
          onBack={goBack}
          action={
            <Button
              size="lg"
              className="w-full"
              onClick={submit}
              disabled={isSubmitting}
            >
              Create and notify client
            </Button>
          }
        >
          {fields}
        </MandateLayout>
      )}
    </CreateMandateForm>
  );
}
